'use strict';

/**
 * VersionHistory — modal/panel listing every render snapshot for this
 * session. Each row links to download or open the rendered PDF.
 */

import React, { useEffect, useState } from 'react';
import * as api from './api';

export default function VersionHistory({ sessionId, open, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !sessionId) return undefined;
    setLoading(true);
    let cancelled = false;
    api
      .listVersions(sessionId)
      .then((rows) => { if (!cancelled) setVersions(rows); })
      .catch((err) => { if (!cancelled) console.warn('[versions] load failed:', err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, sessionId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-30 bg-slate-900/50 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-800">Version history</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        <div className="p-4">
          {loading && <div className="text-sm text-slate-400 text-center py-6">Loading…</div>}
          {!loading && versions.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6">
              No renders yet. Save your markups and click <b>Re-render</b> to produce the first version.
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {versions.map((v, i) => (
              <li key={v.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-800">
                    {v.label || `Version ${versions.length - i}`}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {new Date(v.createdAt).toLocaleString()} · {v.markupCount} markup{v.markupCount === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="flex gap-2">
                  {v.viewUrl && (
                    <a
                      href={v.viewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                    >
                      Open
                    </a>
                  )}
                  {v.downloadUrl && (
                    <a
                      href={v.downloadUrl}
                      className="text-xs px-2 py-1 rounded bg-slate-800 text-white hover:bg-slate-700"
                    >
                      Download
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
